// Shared colors for range-colored score bar charts (ST-2645), reused by the Battery Fleet Quick View
// SOC and Plug-In charts (ST-2646) so every chart draws the exact same shades.
export const SCORE_RANGE_COLORS = {
	red: '#E06666',
	orange: '#F0A04B',
	yellow: '#F2D263',
	lightGreen: '#A8D08D',
	darkGreen: '#4E8F3A'
};

export type ScoreRangeColor = keyof typeof SCORE_RANGE_COLORS;

// Low to high: 0–20% red, 21–40% orange, 41–60% yellow, 61–80% light green, 81% and above dark green.
export const SCORE_RANGE_COLOR_ORDER: ScoreRangeColor[] = ['red', 'orange', 'yellow', 'lightGreen', 'darkGreen'];

// Bar labels are white on the two darkest colors and dark on the rest.
export const SCORE_RANGE_LIGHT_LABEL_COLORS: ScoreRangeColor[] = ['red', 'darkGreen'];

export const SCORE_RANGE_LABEL_COLORS = {
	light: '#FFFFFF',
	dark: '#070707'
};

export type ScoreBand = 'bad' | 'poor' | 'marginal' | 'good' | 'optimal';

export interface ScoreBandRange {
	from: number;
	to: number;
	band: ScoreBand;
}

export const SCORE_BAND_COLORS: Record<ScoreBand, ScoreRangeColor> = {
	bad: 'red',
	poor: 'orange',
	marginal: 'yellow',
	good: 'lightGreen',
	optimal: 'darkGreen',
};

// Utilization / Energy Usage bands, the same 20% color steps as SCORE_RANGE_COLOR_ORDER (ST-2681).
export const UTILIZATION_SCORE_BANDS: ScoreBandRange[] = [
	{ from: 0, to: 20, band: 'bad' },
	{ from: 21, to: 40, band: 'poor' },
	{ from: 41, to: 60, band: 'marginal' },
	{ from: 61, to: 80, band: 'good' },
	{ from: 81, to: Infinity, band: 'optimal' },
];

// Industry Ah Return bands, low to high. Under- and over-charging are both bad, so the colors run
// red → dark green → red. Scores are capped at 150 before bucketing, so the last band is open-ended.
export const AH_RETURN_BANDS: ScoreBandRange[] = [
	{ from: 0, to: 84, band: 'bad' },
	{ from: 85, to: 94, band: 'poor' },
	{ from: 95, to: 99, band: 'marginal' },
	{ from: 100, to: 104, band: 'good' },
	{ from: 105, to: 115, band: 'optimal' },
	{ from: 116, to: 120, band: 'good' },
	{ from: 121, to: 130, band: 'marginal' },
	{ from: 131, to: 149, band: 'poor' },
	{ from: 150, to: Infinity, band: 'bad' },
];

// The band a score falls in. Callers pass the number they show: the gauges pass their rounded score
// (AC-2 / AC-4), so the decimal drop only matters to the bar charts. Below the first band counts as the first band.
export function getScoreBand(bands: ScoreBandRange[], score: number): ScoreBand {
	const value = Math.floor(score || 0);
	return (bands.find(range => value >= range.from && value <= range.to) ?? bands[0]).band;
}
